import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@/fontawesome.ts';
import { ToggleSwitch } from '@/components/admin/events/EventFeatureFilterEditor.tsx';
import {
  MAX_EVENT_REMINDER_COUNT,
  REMINDER_UNIT_LABELS,
  formatReminderOffsetList
} from '@/utils/event-helpers';
import type { ReminderMinutesRow, ReminderUnit } from '@/utils/event-helpers';

interface ReminderMinutesEditorProps {
  rows: ReminderMinutesRow[];
  onChange: (rows: ReminderMinutesRow[]) => void;
  disabled: boolean;
  remindersSent?: number[]; // offsets already enqueued by the backend (edit dialog only)
}

const ALL_REMINDER_UNITS: ReminderUnit[] = ['MINUTES', 'HOURS', 'DAYS'];

const inputClasses =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed';

// Editor for the reminderMinutes offset list of an event or series. The rows ARE the
// state ([] = disabled), so the enable switch needs no state of its own.
export default function ReminderMinutesEditor({ rows, onChange, disabled, remindersSent }: ReminderMinutesEditorProps) {
  const enabled = rows.length > 0;

  const toggleEnabled = () => {
    onChange(enabled ? [] : [{ value: '60', unit: 'MINUTES' }]);
  };

  const updateRow = (index: number, patch: Partial<ReminderMinutesRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...rows, { value: '', unit: 'MINUTES' }]);
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Erinnerungsmails
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Automatische E-Mail-Erinnerungen vor Beginn an alle berechtigten Nutzer
          </p>
        </div>
        <ToggleSwitch checked={enabled} onChange={toggleEnabled} disabled={disabled} />
      </div>

      {enabled && (
        <div className="mt-3 space-y-2">
          {rows.map((row, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                step={1}
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
                className={`w-24 ${inputClasses}`}
                disabled={disabled}
              />
              <select
                value={row.unit}
                onChange={(e) => updateRow(index, { unit: e.target.value as ReminderUnit })}
                className={inputClasses}
                disabled={disabled}
              >
                {ALL_REMINDER_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {REMINDER_UNIT_LABELS[unit]}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-500 dark:text-gray-400">vor Beginn</span>
              <button
                type="button"
                onClick={() => removeRow(index)}
                disabled={disabled}
                className="ml-auto p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                title="Erinnerung entfernen"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={addRow}
              disabled={disabled || rows.length >= MAX_EVENT_REMINDER_COUNT}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlus} className="w-3 h-3 mr-1.5" />
              Erinnerung hinzufügen
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">(max. {MAX_EVENT_REMINDER_COUNT})</span>
          </div>
        </div>
      )}

      {remindersSent !== undefined && remindersSent.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Bereits versendet: {formatReminderOffsetList(remindersSent)} vor Beginn
        </p>
      )}
    </div>
  );
}
